import React, { useCallback, useEffect, useMemo, useState } from "react";

import { toast } from "react-toastify";
import { Check, Loader2, Search, Send } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { cn } from "../../lib/utils";

import toastError from "../../errors/toastError";
import api from "../../services/api";
import { textoDeErro } from "../../errors/textoDeErro";

// O miolo da janela, com toda a escolha de destinos.
//
// Componente separado de propósito: o `DialogContent` do Radix DESMONTA os
// filhos ao fechar, então busca, seleção e falhas somem sozinhas quando a
// janela fecha — por qualquer caminho, inclusive o Esc e o clique fora. Antes
// isso era um `useEffect` que zerava o estado quando `open` virava false: um
// efeito que só existia para desfazer o que o próprio React desfaz de graça,
// e que a regra `react-hooks/set-state-in-effect` acusa com razão (setState
// síncrono dentro de efeito = renderização em cascata).
const ConteudoDoEncaminhamento = ({ onClose, onEnviado, message, messages }) => {
  // Uma mensagem (menu da bolha) ou várias (modo seleção da conversa). O
  // resto da tela não muda: os destinos e os erros por destino são os mesmos.
  const lista = messages?.length ? messages : message ? [message] : [];
  const varias = lista.length > 1;
  const [busca, setBusca] = useState("");
  const [contatos, setContatos] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [selecionados, setSelecionados] = useState([]);
  const [enviando, setEnviando] = useState(false);
  // Resultado por destino: com cinco contatos e um número inválido, os outros
  // quatro já receberam e quem atende precisa saber qual não foi.
  const [falhas, setFalhas] = useState([]);

  const procurar = useCallback(async () => {
    if (busca.trim().length < 2) {
      setContatos([]);
      return;
    }

    setBuscando(true);
    try {
      const { data } = await api.get("/contacts", {
        params: { searchParam: busca.trim() },
      });
      setContatos((data.contacts || []).slice(0, 20));
    } catch (err) {
      toastError(err);
    }
    setBuscando(false);
  }, [busca]);

  useEffect(() => {
    // Espera a digitação parar: uma consulta por tecla castigaria o servidor.
    const t = setTimeout(procurar, 400);

    return () => clearTimeout(t);
  }, [procurar]);

  // Mensagem NOSSA vem com a assinatura do atendente ("*Fulano:*" na primeira
  // linha) quando a conexão assina. O backend tira o mesmo prefixo, com a
  // mesma regra, antes de mandar — aqui é só a prévia. Recebida não é tocada:
  // "*Urgente:*" é realce do cliente, não assinatura nossa.
  // A prévia descreve a PRIMEIRA da lista — com uma só, é ela mesma.
  const primeira = lista[0];

  const textoLimpo = primeira?.fromMe
    ? String(primeira?.body || "").replace(/^\*[^*\n]+:\*\n/, "")
    : String(primeira?.body || "");

  const previa = useMemo(() => {
    if (textoLimpo.trim()) return textoLimpo;

    const rotulos = {
      image: "📷 Imagem",
      video: "🎥 Vídeo",
      audio: "🎧 Áudio",
      sticker: "🙂 Figurinha",
      location: "📍 Localização",
      vcard: "👤 Contato",
    };

    return rotulos[primeira?.mediaType] || "📎 Arquivo";
  }, [textoLimpo, primeira?.mediaType]);

  const alternar = (contato) => {
    setSelecionados((atual) =>
      atual.some((c) => c.id === contato.id)
        ? atual.filter((c) => c.id !== contato.id)
        : [...atual, contato],
    );
  };

  const encaminhar = async () => {
    if (!selecionados.length || enviando) return;

    setEnviando(true);
    setFalhas([]);
    try {
      // Rota da lista, mesmo com uma só: o backend resolve o atendimento de
      // destino uma vez e manda todas na ordem da conversa.
      const { data } = await api.post("/messages/forward", {
        messageIds: lista.map((m) => m.id),
        contactIds: selecionados.map((c) => c.id),
      });

      const resultados = data?.resultados || [];
      const naoForam = resultados.filter((r) => !r.ok);
      const foram = resultados.length - naoForam.length;

      if (foram) {
        const quantas = varias ? `${lista.length} mensagens` : "Encaminhado";
        toast.success(
          foram === 1
            ? `${quantas} para 1 contato.`
            : `${quantas} para ${foram} contatos.`,
        );
      }

      if (naoForam.length) {
        // A janela fica aberta com a lista do que falhou: fechar esconderia o
        // único aviso de que alguém não recebeu.
        setFalhas(
          naoForam.map((r) => ({
            id: r.id,
            nome: selecionados.find((c) => c.id === r.id)?.name || `#${r.id}`,
            erro: r.erro,
          })),
        );
        setSelecionados((atual) =>
          atual.filter((c) => naoForam.some((r) => r.id === c.id)),
        );
      } else {
        // Deu tudo certo: além de fechar, avisa quem abriu — é o que faz o
        // modo seleção sair sozinho, em vez de deixar as mensagens marcadas
        // como se ainda houvesse algo a fazer com elas.
        onEnviado?.();
        onClose();
      }
    } catch (err) {
      toastError(err);
    }
    setEnviando(false);
  };

  return (
    <>
      <DialogHeader className="border-b px-4 py-3">
        <DialogTitle className="text-base">
          {varias
            ? `Encaminhar ${lista.length} mensagens`
            : "Encaminhar mensagem"}
        </DialogTitle>
        <p className="line-clamp-2 text-xs text-muted-foreground">
          {previa}
          {varias && ` + ${lista.length - 1} outra${lista.length > 2 ? "s" : ""}`}
        </p>
      </DialogHeader>

      {falhas.length > 0 && (
        <div className="border-b bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          <p className="font-medium">Não foi para:</p>
          <ul className="mt-1 space-y-0.5">
            {falhas.map((falha) => (
              <li key={falha.id}>
                {falha.nome} — {textoDeErro(falha.erro)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border-b p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            autoFocus
            placeholder="Buscar contato pelo nome ou número..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 space-y-1 overflow-y-auto p-3">
        {/* Quem já foi escolhido continua à vista mesmo depois de a busca
            mudar: sem isto o atendente marcava três contatos, digitava o
            quarto nome e não sabia mais quem estava selecionado. */}
        {selecionados.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1">
            {selecionados.map((contato) => (
              <button
                key={contato.id}
                type="button"
                onClick={() => alternar(contato)}
                className="rounded-full bg-accent px-2 py-1 text-xs hover:bg-accent/70"
              >
                {contato.name} ✕
              </button>
            ))}
          </div>
        )}

        {buscando ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : contatos.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {busca.trim().length < 2
              ? "Digite ao menos duas letras para buscar."
              : "Nenhum contato com esse nome."}
          </p>
        ) : (
          contatos.map((contato) => {
            const marcado = selecionados.some((c) => c.id === contato.id);

            return (
              <button
                key={contato.id}
                type="button"
                disabled={enviando}
                onClick={() => alternar(contato)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-lg p-2 text-left",
                  "hover:bg-accent disabled:opacity-60",
                  marcado && "bg-accent",
                )}
              >
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage
                    src={contato.profilePicUrl}
                    alt={contato.name}
                  />
                  <AvatarFallback>
                    {(contato.name || "?").slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {contato.name}
                  </p>
                  <p className="truncate text-xs tabular-nums text-muted-foreground">
                    {contato.number || "sem número"}
                  </p>
                </div>

                {marcado && (
                  <Check className="h-4 w-4 shrink-0 text-primary" />
                )}
              </button>
            );
          })
        )}
      </div>

      <DialogFooter className="border-t p-3">
        <Button variant="outline" onClick={onClose} disabled={enviando}>
          Cancelar
        </Button>
        <Button
          onClick={encaminhar}
          disabled={!selecionados.length || enviando}
        >
          {enviando ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Send className="mr-2 h-4 w-4" />
          )}
          Encaminhar
          {selecionados.length > 0 && ` (${selecionados.length})`}
        </Button>
      </DialogFooter>
    </>
  );
};

// Encaminhar uma mensagem para outros contatos.
//
// Quem reenvia é o backend (`POST /messages/:id/forward`): ele abre (ou
// reaproveita) o atendimento do destino e manda a mensagem com o MESMO tipo —
// imagem, vídeo, áudio, documento. Antes a tela mandava `message.body` como
// texto novo, e por isso só dava para encaminhar mensagem de texto.
const ForwardMessageModal = ({ open, onClose, onEnviado, message, messages }) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="flex max-h-[80vh] flex-col gap-0 p-0 sm:max-w-md">
      <ConteudoDoEncaminhamento
        onClose={onClose}
        onEnviado={onEnviado}
        message={message}
        messages={messages}
      />
    </DialogContent>
  </Dialog>
);

export default ForwardMessageModal;
